import { Component, OnInit, Signal, inject, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { Validators } from "@angular/forms";

import {
  faCheckCircle,
  faCircleExclamation,
  faCog,
  faGlobe,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import { LoginService } from "../core/services/login.service";
import { SessionStorageService } from "../core/services/session-storage.service";
import { ControlValuesMatchValidator } from "../core/validators/control-values-match.directive";
import { ManagementUser } from "../core/models/management-user.model";
import { UsersService } from "../core/services/users.service";

@Component({
  selector: "wf-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.css"],
  standalone: false,
})
export class SettingsComponent implements OnInit {
  private loginService = inject(LoginService);
  private usersService = inject(UsersService);
  private sessionStorageService = inject(SessionStorageService);
  private fb = inject(FormBuilder);

  userData: ManagementUser | null = null;
  isFILUser: boolean | undefined = false;
  demoModeEnabled: Signal<boolean> = inject(SessionStorageService).isDemoMode;

  protected readonly isChangingPassword = signal(false);
  protected readonly passwordSuccessMessage = signal("");
  protected readonly isChangingTimezone = signal(false);
  protected readonly timezoneSuccessMessage = signal("");

  changePasswordForm = this.fb.group(
    {
      currentPassword: ["", Validators.required],
      newPassword: [
        "",
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          Validators.pattern(
            /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9_!?]){8,20}$/,
          ),
        ]),
      ],
      confirmPassword: ["", Validators.required],
    },
    {
      validator: ControlValuesMatchValidator("newPassword", "confirmPassword"),
    },
  );
  changeTimezoneForm = new FormGroup({
    timezone: new FormControl(""),
  });

  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  successIcon = faCheckCircle;
  errorIcon = faCircleExclamation;
  settingsIcon = faCog;
  timezoneIcon = faGlobe;
  loadingIcon = faSpinner;

  timezones = [
    { code: "America/New_York", display: "Eastern" },
    { code: "America/Chicago", display: "Central" },
    { code: "America/Denver", display: "Mountain" },
    { code: "America/Los_Angeles", display: "Pacific" },
    { code: "US/Arizona", display: "Arizona" },
    { code: "America/Anchorage", display: "Alaska" },
    { code: "US/Hawaii", display: "Hawaii" },
  ] as const;

  ngOnInit(): void {
    this.userData = this.sessionStorageService.userData();
    if (this.userData) {
      this.isFILUser = this.userData.isFILUser();
      this.changeTimezoneForm.controls.timezone.setValue(this.userData.timeZoneId);
    }
  }

  changePassword() {
    this.isChangingPassword.set(true);
    this.passwordSuccessMessage.set("");

    this.loginService
      .changePassword(this.currentPassword?.value, this.newPassword?.value)
      .subscribe({
        next: (status) => {
          this.isChangingPassword.set(false);

          if (status === 200) {
            this.passwordSuccessMessage.set("Password Successfully Updated!");
            this.currentPassword?.setErrors(null);
            this.newPassword?.setErrors(null);
            this.confirmPassword?.setErrors(null);
          } else if (status === 400) {
            this.newPassword?.setErrors({ pattern: true });
          } else if (status === 401) {
            this.currentPassword?.setErrors({ incorrect: true });
          }
        },
      });
  }

  changeTimezone() {
    const timeZoneId = this.changeTimezoneForm.controls.timezone.value;

    this.isChangingTimezone.set(true);

    if (!this.userData) {
      console.error("User data not found");
    }
    // TODO: remove 'as' when TS can properly type-narrow this.userData
    const currentUser = this.userData as ManagementUser;

    this.usersService
      .editOwnUser({ ...currentUser, timeZoneId })
      .subscribe({
        next: () => {
          currentUser.timeZoneId = timeZoneId;
          this.sessionStorageService.setUserData(currentUser);
          this.timezoneSuccessMessage.set("Timezone Successfully Updated!");
          this.isChangingTimezone.set(false);
        },
        error: () => {},
      });

    this.timezoneSuccessMessage.set("");
  }

  currentPasswordToggle() {
    this.showCurrentPassword = !this.showCurrentPassword;

    return this.showCurrentPassword;
  }
  newPasswordToggle() {
    this.showNewPassword = !this.showNewPassword;

    return this.showNewPassword;
  }

  confirmPasswordToggle() {
    this.showConfirmPassword = !this.showConfirmPassword;

    return this.showConfirmPassword;
  }

  toggleManagementDemoMode() {
    const userData = this.sessionStorageService.userData();
    if (userData) {
      this.sessionStorageService.toggleDemoModeEnabled();
      this.sessionStorageService.setUserData(userData);
    }
  }

  // Utility methods to get our form controls
  get currentPassword() {
    return this.changePasswordForm.get("currentPassword");
  }

  get newPassword() {
    return this.changePasswordForm.get("newPassword");
  }

  get confirmPassword() {
    return this.changePasswordForm.get("confirmPassword");
  }
}
